import { useWalletsMe } from '../../../api';
import { Account } from '../../../models';
import { UseWalletAccountsByCoinTypeProps } from './props';
import { useMemo } from 'react';

/**
 * Custom hook to filter wallet accounts by coin type.
 * This hook retrieves the wallet information and filters the accounts based on the specified coin type.
 *
 * @param {UseWalletAccountsByCoinTypeProps} props - The properties for the hook.
 * @param {string} props.coinType - The type of the coin to filter accounts by.
 * @param {boolean} [props.enabled=true] - Flag to enable or disable the hook.
 * @returns {Object} An object containing the wallet information and filtered accounts.
 *
 * @example
 * // Usage example:
 * const { wallet, accounts } = useWalletAccountsByCoinType({ coinType: 'BTC', enabled: true });
 */
export function useWalletAccountsByCoinType({
  coinType,
  enabled = true,
}: UseWalletAccountsByCoinTypeProps) {
  // Retrieve the wallet information and other properties using the useWalletsMe hook
  const { wallet, ...rest } = useWalletsMe({
    enabled,
  });

  // Memoize the filtered accounts based on the coin type and wallet information
  const accounts: Account[] = useMemo(
    () => wallet?.accounts?.filter(account => account?.coin?.type == coinType),
    [coinType, wallet],
  );

  // Return the wallet information, filtered accounts, and other properties
  return {
    ...rest,
    wallet,
    accounts,
  };
}

export type { UseWalletAccountsByCoinTypeProps };
